import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Book, Truck, CreditCard, Mic, ChevronRight, ArrowLeft, Package, Headphones, FileText, CheckCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import VoiceRecorder from './VoiceRecorder';

const STEPS = ['Preview', 'Voice', 'Shipping', 'Payment'];

const BundleOrderFlow = () => {
  const { autobiographyId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [step, setStep] = useState(0);
  const [autobiography, setAutobiography] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [voiceData, setVoiceData] = useState(null);
  const [shipping, setShipping] = useState({
    shipping_name: '', street1: '', city: '', state_code: '',
    country_code: 'GB', postcode: '', phone_number: '', contact_email: ''
  });

  const cancelled = searchParams.get('cancelled');

  useEffect(() => {
    fetchAutobiography();
    if (cancelled) toast({ title: 'Payment Cancelled', description: 'You can try again when ready.', variant: 'destructive' });
  }, [autobiographyId]);

  const fetchAutobiography = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/autobiography/${autobiographyId}`);
      if (res.ok) setAutobiography(await res.json());
      else navigate('/dashboard');
    } catch { navigate('/dashboard'); }
    finally { setLoading(false); }
  };

  const handleShippingChange = (e) => setShipping(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleCheckout = async () => {
    const required = ['shipping_name', 'street1', 'city', 'country_code', 'postcode', 'contact_email'];
    for (const f of required) {
      if (!shipping[f]?.trim()) {
        toast({ title: 'Missing Info', description: `Please fill in ${f.replace(/_/g, ' ')}`, variant: 'destructive' });
        return;
      }
    }
    if (!voiceData) {
      toast({ title: 'Voice Required', description: 'Please record or upload your voice sample first.', variant: 'destructive' });
      return;
    }
    setProcessing(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bundle/checkout`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ autobiography_id: autobiographyId, ...shipping }),
      });
      const data = await res.json();
      if (data.checkout_url) {
        // Store voice data temporarily so we can use it after payment
        sessionStorage.setItem(`bundle_voice_${data.order_id}`, voiceData);
        window.location.href = data.checkout_url;
      } else throw new Error(data.detail || 'Checkout failed');
    } catch (err) {
      toast({ title: 'Checkout Error', description: err.message, variant: 'destructive' });
      setProcessing(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-purple-200 border-t-purple-500 rounded-full animate-spin" />
    </div>
  );

  if (!autobiography) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 py-8 px-4" data-testid="bundle-order-flow">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4 transition-colors" data-testid="bundle-back-btn">
          <ArrowLeft className="w-5 h-5" /><span className="font-medium">Back</span>
        </button>
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-2">The Complete Life Echo Bundle</h1>
        <p className="text-gray-600">Hardcover book + AI audiobook in your voice + digital PDF</p>
      </div>

      {/* Progress Steps */}
      <div className="max-w-4xl mx-auto mb-10">
        <div className="flex items-center justify-center gap-2 flex-wrap">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${i <= step ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-md' : 'bg-white/60 text-gray-400'}`} data-testid={`bundle-step-${i}`}>
                {i === 0 && <Book className="w-4 h-4" />}
                {i === 1 && <Mic className="w-4 h-4" />}
                {i === 2 && <Truck className="w-4 h-4" />}
                {i === 3 && <CreditCard className="w-4 h-4" />}
                <span className="hidden sm:inline">{s}</span>
              </div>
              {i < STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-gray-300" />}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="max-w-4xl mx-auto">
        {/* Step 0: Bundle Preview */}
        {step === 0 && (
          <div className="fade-in" data-testid="bundle-step-preview">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Visual */}
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 shadow-2xl flex flex-col items-center justify-center min-h-[420px] relative overflow-hidden">
                <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, white 10px, white 11px)' }} />
                <div className="flex gap-4 items-end z-10">
                  <div className="bg-white rounded-lg p-4 w-32 shadow-lg transform rotate-[-3deg]">
                    <div className="border-b border-pink-200 pb-2 mb-2">
                      <p className="text-[8px] font-bold text-gray-800 leading-tight">{autobiography.title}</p>
                    </div>
                    <div className="flex items-center gap-1 text-[6px] text-gray-400">
                      <Book className="w-2 h-2" />
                      <span>Hardcover</span>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-pink-100 to-purple-100 rounded-lg p-4 w-28 shadow-lg transform rotate-[2deg]">
                    <div className="flex items-center justify-center mb-2">
                      <Headphones className="w-8 h-8 text-purple-500" />
                    </div>
                    <p className="text-[7px] text-center text-purple-700 font-medium">Audiobook</p>
                    <p className="text-[6px] text-center text-purple-500">In Your Voice</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 w-24 shadow-lg transform rotate-[1deg]">
                    <div className="flex items-center justify-center mb-2">
                      <FileText className="w-6 h-6 text-pink-400" />
                    </div>
                    <p className="text-[7px] text-center text-gray-600 font-medium">Digital PDF</p>
                  </div>
                </div>
                <div className="mt-6 text-white/60 text-xs text-center z-10">
                  {autobiography.word_count?.toLocaleString()} words &middot; {autobiography.chapters} chapters
                </div>
              </div>

              {/* Details */}
              <div className="space-y-6">
                <div>
                  <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-400 to-orange-400 text-white px-4 py-1.5 rounded-full mb-4 text-sm font-medium shadow-sm">
                    <Package className="w-3.5 h-3.5" />
                    <span>Best Value</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-1">{autobiography.title}</h3>
                  <p className="text-gray-500 italic">{autobiography.subtitle}</p>
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-700">Everything Included:</h4>
                  {[
                    { icon: Book, text: 'Professional hardcover book, delivered to your door' },
                    { icon: Headphones, text: 'AI audiobook narrated in your own voice' },
                    { icon: FileText, text: 'Digital PDF download, available instantly' },
                    { icon: CheckCircle, text: `${autobiography.chapters} beautifully formatted chapters` },
                    { icon: CheckCircle, text: 'Elegant cover with gold accents' },
                    { icon: CheckCircle, text: 'Lifetime access to all formats' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm text-gray-600">
                      <item.icon className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>{item.text}</span>
                    </div>
                  ))}
                </div>

                <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-6 border border-amber-200">
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-4xl font-bold text-gray-800">£144.99</span>
                    <span className="text-sm text-gray-400 line-through">£183</span>
                  </div>
                  <p className="text-sm text-amber-700">Save over £38 with the complete bundle</p>
                </div>

                <Button
                  onClick={() => setStep(1)}
                  className="w-full btn-primary text-white py-5 rounded-full border-0 text-base font-semibold"
                  data-testid="bundle-continue-voice"
                >
                  Continue <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 1: Voice Recording */}
        {step === 1 && (
          <div className="fade-in max-w-2xl mx-auto" data-testid="bundle-step-voice">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h3 className="text-xl font-bold text-gray-800 mb-2 flex items-center gap-2">
                <Mic className="w-5 h-5 text-purple-500" /> Record Your Voice
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                We'll use AI to clone your voice and narrate your entire autobiography.
                The more natural you sound, the better the result.
              </p>

              <VoiceRecorder
                onVoiceReady={(base64Data) => setVoiceData(base64Data)}
                disabled={processing}
              />

              <div className="flex gap-3 mt-8">
                <Button variant="outline" onClick={() => setStep(0)} className="flex-1 py-5 rounded-full" data-testid="bundle-back-to-preview">
                  Back
                </Button>
                <Button
                  onClick={() => {
                    if (!voiceData) {
                      toast({ title: 'Voice Required', description: 'Please record or upload your voice first.', variant: 'destructive' });
                      return;
                    }
                    setStep(2);
                  }}
                  className="flex-1 btn-primary text-white py-5 rounded-full border-0 font-semibold"
                  data-testid="bundle-continue-shipping"
                  disabled={!voiceData}
                >
                  Continue to Shipping <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Shipping */}
        {step === 2 && (
          <div className="fade-in max-w-2xl mx-auto" data-testid="bundle-step-shipping">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <Truck className="w-5 h-5 text-purple-500" /> Shipping Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2"><Label>Full Name *</Label><Input name="shipping_name" value={shipping.shipping_name} onChange={handleShippingChange} placeholder="John Smith" className="mt-1 input-focus" data-testid="bundle-input-name" /></div>
                <div className="md:col-span-2"><Label>Email *</Label><Input name="contact_email" value={shipping.contact_email} onChange={handleShippingChange} placeholder="john@example.com" type="email" className="mt-1 input-focus" data-testid="bundle-input-email" /></div>
                <div className="md:col-span-2"><Label>Street Address *</Label><Input name="street1" value={shipping.street1} onChange={handleShippingChange} placeholder="123 Main Street" className="mt-1 input-focus" data-testid="bundle-input-street" /></div>
                <div><Label>City *</Label><Input name="city" value={shipping.city} onChange={handleShippingChange} placeholder="London" className="mt-1 input-focus" data-testid="bundle-input-city" /></div>
                <div><Label>Postcode *</Label><Input name="postcode" value={shipping.postcode} onChange={handleShippingChange} placeholder="SW1A 1AA" className="mt-1 input-focus" data-testid="bundle-input-postcode" /></div>
                <div><Label>Country Code *</Label><Input name="country_code" value={shipping.country_code} onChange={handleShippingChange} placeholder="GB" className="mt-1 input-focus" data-testid="bundle-input-country" /></div>
                <div><Label>State/Region</Label><Input name="state_code" value={shipping.state_code} onChange={handleShippingChange} placeholder="Optional" className="mt-1 input-focus" data-testid="bundle-input-state" /></div>
                <div className="md:col-span-2"><Label>Phone Number</Label><Input name="phone_number" value={shipping.phone_number} onChange={handleShippingChange} placeholder="+44 7700 900000" className="mt-1 input-focus" data-testid="bundle-input-phone" /></div>
              </div>

              {/* Order Summary */}
              <div className="mt-8 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-5">
                <h4 className="font-semibold text-gray-800 mb-3">Bundle Summary</h4>
                <div className="flex justify-between text-sm text-gray-600 mb-1"><span>Hardcover Book</span><span>£59</span></div>
                <div className="flex justify-between text-sm text-gray-600 mb-1"><span>AI Audiobook (Your Voice)</span><span>£60</span></div>
                <div className="flex justify-between text-sm text-gray-600 mb-1"><span>Digital PDF</span><span>£25</span></div>
                <div className="flex justify-between text-sm text-gray-600 mb-1"><span>Bundle Discount</span><span className="text-green-600">-£38+</span></div>
                <div className="flex justify-between text-sm text-gray-600 mb-2"><span>Shipping</span><span>Included</span></div>
                <div className="border-t pt-2 flex justify-between font-bold text-gray-800"><span>Total</span><span>£144.99</span></div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1 py-5 rounded-full" data-testid="bundle-back-to-voice">Back</Button>
                <Button onClick={() => setStep(3)} className="flex-1 btn-primary text-white py-5 rounded-full border-0 font-semibold" data-testid="bundle-continue-payment">
                  Continue to Payment <CreditCard className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Payment Confirmation */}
        {step === 3 && (
          <div className="fade-in max-w-2xl mx-auto" data-testid="bundle-step-payment">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-purple-500" /> Confirm Your Bundle Order
              </h3>

              <div className="space-y-4 mb-6">
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm text-gray-500 mb-1">Bundle Contents</p>
                  <div className="space-y-1">
                    <p className="font-semibold text-gray-800 flex items-center gap-2"><Book className="w-4 h-4 text-purple-500" /> Hardcover Book</p>
                    <p className="font-semibold text-gray-800 flex items-center gap-2"><Headphones className="w-4 h-4 text-purple-500" /> AI Audiobook (Your Voice)</p>
                    <p className="font-semibold text-gray-800 flex items-center gap-2"><FileText className="w-4 h-4 text-purple-500" /> Digital PDF</p>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm text-gray-500 mb-1">Shipping to</p>
                  <p className="font-semibold text-gray-800">{shipping.shipping_name}</p>
                  <p className="text-sm text-gray-600">{shipping.street1}, {shipping.city}, {shipping.postcode}, {shipping.country_code}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm text-gray-500 mb-1">Voice Sample</p>
                  <p className="font-semibold text-gray-800 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" /> Recorded and ready
                  </p>
                </div>
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4">
                  <div className="flex justify-between font-bold text-lg text-gray-800"><span>Total</span><span>£144.99</span></div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(2)} className="flex-1 py-5 rounded-full" data-testid="bundle-back-to-shipping">Back</Button>
                <Button onClick={handleCheckout} disabled={processing} className="flex-1 btn-primary text-white py-5 rounded-full border-0 font-semibold" data-testid="bundle-pay-btn">
                  {processing ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto" /> : <><Package className="w-4 h-4 mr-2" /> Pay £144.99 & Order Bundle</>}
                </Button>
              </div>

              <p className="text-center text-xs text-gray-400 mt-4">
                Secure payment via Stripe. Your book will ship in 7-10 business days. Audiobook will be available for download once generated.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BundleOrderFlow;
